import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard, BookOpen, AlertTriangle, FileText, Bug,
  Settings, LogOut, Menu, X, Shield, Moon, Sun, Bell, ChevronLeft,
  ChevronDown, ChevronRight as ChevronRightIcon, User, Users,
  FileCheck, Link2, ClipboardCheck, Paperclip, Wrench, CalendarDays,
  Check, BarChart3, ShieldAlert, Mail, Target, Sparkles, Award, Building2,
  GraduationCap, TrendingUp, Brain, Cloud, Globe, Upload, CreditCard, PenLine, Crown,
  Search, Pin, PinOff, Star, Package, FlaskConical, Leaf, Warehouse, Layers,
} from "lucide-react";
import { Button } from "../ui/button";
import Logo from "../Logo";
import { ScrollArea } from "../ui/scroll-area";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger
} from "../ui/dropdown-menu";
import { Badge } from "../ui/badge";
import { Lock } from "lucide-react";
import { useAuth, useTheme, API } from "../../App";
import { useI18n } from "../../i18n/I18nProvider";
import { useSubscription } from "../../contexts/SubscriptionProvider";
import { CommandPalette, useCommandPalette } from "../CommandPalette";
import { AICopilot } from "../AICopilot";
import TwoFactorBanner from "../TwoFactorBanner";

const SidebarLayout = ({ children }) => {
  const { user, token, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { t, locale, setLocale } = useI18n();
  const { hasFeature } = useSubscription();
  const navigate = useNavigate();
  const location = useLocation();

  // Detect macOS to render the right shortcut hint (⌘K vs Ctrl+K)
  const isMac = typeof navigator !== "undefined" && (
    /Mac|iPhone|iPad|iPod/i.test(
      navigator.userAgentData?.platform ?? navigator.platform ?? navigator.userAgent ?? ""
    )
  );

  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [showNotifs, setShowNotifs] = useState(false);
  const { open: cmdOpen, setOpen: setCmdOpen } = useCommandPalette();
  const [pinned, setPinned] = useState(() => {
    try {
      const raw = localStorage.getItem("complianceos_nav_pinned");
      return raw ? JSON.parse(raw) : ["/dashboard"];
    } catch { return ["/dashboard"]; }
  });

  const togglePin = useCallback((path) => {
    setPinned((prev) => {
      const next = prev.includes(path) ? prev.filter((p) => p !== path) : [...prev, path];
      try { localStorage.setItem("complianceos_nav_pinned", JSON.stringify(next)); } catch {}
      return next;
    });
  }, []);

  const isPinned = useCallback((path) => pinned.includes(path), [pinned]);
  const [openGroups, setOpenGroups] = useState(() => {
    const saved = localStorage.getItem("complianceos_nav_groups");
    return saved ? JSON.parse(saved) : { overview: true, compliance: true, risk_threats: true, governance: true };
  });

  const fetchNotifCount = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/notifications/count`, { headers: { Authorization: `Bearer ${token}` } });
      setUnreadCount(res.data.unread || 0);
    } catch {}
  }, [token]);

  const fetchNotifications = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/notifications`, { headers: { Authorization: `Bearer ${token}` } });
      setNotifications(res.data.slice(0, 20));
    } catch {}
  }, [token]);

  const markAllRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch {}
  };

  useEffect(() => {
    fetchNotifCount();
    const interval = setInterval(fetchNotifCount, 60000);
    return () => clearInterval(interval);
  }, [fetchNotifCount]);

  useEffect(() => {
    localStorage.setItem("complianceos_nav_groups", JSON.stringify(openGroups));
  }, [openGroups]);

  const navGroups = [
    {
      key: "overview", labelKey: "nav.overview", icon: LayoutDashboard,
      items: [
        { icon: LayoutDashboard, labelKey: "nav.dashboard", path: "/dashboard" },
        { icon: CalendarDays, labelKey: "nav.calendar", path: "/calendar" },
      ]
    },
    {
      key: "compliance", labelKey: "nav.compliance", icon: Shield,
      items: [
        { icon: BookOpen, labelKey: "nav.controls", path: "/controls" },
        { icon: FileCheck, labelKey: "nav.soa", path: "/soa" },
        { icon: Link2, labelKey: "nav.mappings", path: "/mappings" },
        { icon: Target, labelKey: "nav.gap_analysis", path: "/gap-analysis" },
        { icon: Award, labelKey: "nav.certification", path: "/certification" },
      ]
    },
    {
      key: "risk_threats", labelKey: "nav.risk_threats", icon: AlertTriangle,
      items: [
        { icon: AlertTriangle, labelKey: "nav.risks", path: "/risks" },
        { icon: BarChart3, labelKey: "nav.heatmap", path: "/risk-heatmap" },
        { icon: ShieldAlert, labelKey: "nav.threat_intel", path: "/threat-intel", feature: "threat_intelligence" },
        { icon: Shield, labelKey: "nav.vuln_scanner", path: "/vuln-scanner" },
        { icon: Brain, labelKey: "nav.risk_predictor", path: "/risk-predictor", feature: "ai_risk_predictor" },
        { icon: Bug, labelKey: "nav.incidents", path: "/incidents" },
      ]
    },
    {
      key: "governance", labelKey: "nav.governance", icon: ClipboardCheck,
      items: [
        { icon: FileText, labelKey: "nav.documents", path: "/documents" },
        { icon: Paperclip, labelKey: "nav.evidence", path: "/evidence" },
        { icon: ClipboardCheck, labelKey: "nav.audits", path: "/audits" },
        { icon: Users, labelKey: "nav.mgmt_review", path: "/management-review" },
        { icon: Wrench, labelKey: "nav.corrective_actions", path: "/corrective-actions" },
      ]
    },
    {
      key: "organization", labelKey: "nav.organization", icon: Building2,
      items: [
        { icon: Building2, labelKey: "nav.vendors", path: "/vendor-risk", feature: "vendor_risk" },
        { icon: GraduationCap, labelKey: "nav.training", path: "/training" },
        { icon: CalendarDays, labelKey: "nav.maintenance", path: "/maintenance" },
      ]
    },
    {
      key: "reports", labelKey: "nav.reports", icon: BarChart3,
      items: [
        { icon: BarChart3, labelKey: "nav.reports_export", path: "/reports" },
        { icon: FileText, labelKey: "nav.exec_report", path: "/exec-report", feature: "executive_report" },
        { icon: TrendingUp, labelKey: "nav.benchmarking", path: "/benchmarking" },
      ]
    },
    {
      key: "ai_tools", labelKey: "nav.ai_tools", icon: Sparkles,
      items: [
        { icon: Sparkles, labelKey: "nav.policy_gen", path: "/policy-generator", feature: "ai_policy_generator" },
        { icon: Target, labelKey: "nav.wizard", path: "/wizard", feature: "ai_implementation_wizard" },
        { icon: Cloud, labelKey: "nav.evidence_collection", path: "/evidence-collection", feature: "evidence_cloud_connectors" },
      ]
    },
    {
      key: "zona_franca", labelKey: "nav.zona_franca", icon: Warehouse,
      feature: "zona_franca",
      items: [
        { icon: Warehouse, labelKey: "nav.zf_overview", path: "/zona-franca" },
        { icon: Shield, labelKey: "nav.zf_oea", path: "/zona-franca/oea" },
        { icon: Building2, labelKey: "nav.zf_cnzfe", path: "/zona-franca/cnzfe" },
        { icon: Leaf, labelKey: "nav.zf_iso22000", path: "/zona-franca/iso22000" },
        { icon: FlaskConical, labelKey: "nav.zf_bpm", path: "/zona-franca/bpm" },
        { icon: AlertTriangle, labelKey: "nav.zf_haccp", path: "/zona-franca/haccp" },
        { icon: Package, labelKey: "nav.zf_nordom477", path: "/zona-franca/nordom477" },
      ]
    },
    {
      key: "settings", labelKey: "nav.settings", icon: Settings,
      items: [
        { icon: Mail, labelKey: "nav.email", path: "/email-settings", feature: "smtp_notifications" },
        { icon: PenLine, labelKey: "nav.esignature", path: "/esignature-settings", feature: "esignature" },
        { icon: Users, labelKey: "nav.rbac", path: "/rbac", feature: "rbac_custom_roles" },
        { icon: Building2, labelKey: "nav.tenants", path: "/tenants", feature: "multi_tenant" },
        { icon: CreditCard, labelKey: "nav.billing", path: "/billing" },
        // D-S17-017: only owner/admin may manage framework selection
        { icon: Layers, labelKey: "nav.frameworks", path: "/settings/frameworks", roleRequired: ["owner", "admin"] },
        { icon: Link2, labelKey: "nav.portal", path: "/portal" },
        { icon: Upload, labelKey: "nav.bulk", path: "/bulk", feature: "bulk_import_export" },
      ]
    },
  ];

  // Platform admin group (only visible to super-admins)
  if (user?.is_super_admin) {
    navGroups.push({
      key: "platform",
      labelKey: "nav.platform_admin",
      icon: Crown,
      superAdminOnly: true,
      items: [
        { icon: Crown, labelKey: "nav.platform_admin", path: "/platform-admin" },
      ],
    });
  }

  const toggleGroup = (key) => {
    setOpenGroups(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const isActive = (path) => location.pathname === path;
  const isGroupActive = (group) => group.items.some(item => location.pathname === item.path);

  const handleLogout = () => { logout(); navigate("/"); };

  const SidebarContent = () => (
    <>
      {/* Logo — S9/S11: V2 assets. Navy chip so the white wordmark/symbol stay
          visible on the light sidebar surface; wordmark uses w-full to fit the
          narrow expanded sidebar (w-60) without clipping the "OS". */}
      <div className={`${collapsed ? 'p-2' : 'p-4'} border-b border-border`}>
        {collapsed ? (
          <div className="flex justify-center">
            <div className="bg-[#0A0F26] rounded-lg p-1.5 flex items-center justify-center">
              <Logo variant="mark" className="w-8 h-8" alt="ComplianceOS" />
            </div>
          </div>
        ) : (
          <div className="bg-[#0A0F26] rounded-lg px-3 py-2.5 flex items-center">
            <Logo variant="wordmark" className="text-lg" alt="ComplianceOS" />
          </div>
        )}
      </div>

      {/* Navigation */}
      <ScrollArea className="flex-1 py-2">
        <nav className="px-2 space-y-0.5" aria-label={t("nav.overview")}>
          {/* Pinned Items */}
          {pinned.length > 0 && (
            <div className="mb-2">
              {!collapsed && (
                <div className="px-3 py-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  <Star className="w-3.5 h-3.5" aria-hidden="true" />
                  <span>{locale === "es" ? "Anclados" : "Pinned"}</span>
                </div>
              )}
              {pinned.map((path) => {
                // resolve item from all groups
                const allItems = navGroups.flatMap((g) => g.items);
                const item = allItems.find((it) => it.path === path);
                if (!item) return null;
                const Icon = item.icon;
                const active = isActive(item.path);
                return (
                  <div key={`pin-${path}`} className="group relative">
                    <Button
                      variant="ghost"
                      size="sm"
                      className={`w-full justify-start gap-2 h-8 ${collapsed ? 'px-3' : 'px-4 ml-3'} ${
                        active ? 'bg-primary/10 text-primary font-medium' : 'text-muted-foreground hover:text-foreground'
                      }`}
                      onClick={() => { navigate(item.path); setMobileOpen(false); }}
                    >
                      <Icon className={`w-4 h-4 flex-shrink-0 ${active ? 'text-primary' : ''}`} aria-hidden="true" />
                      {!collapsed && (
                        <span className="truncate text-xs flex-1 text-left">{t(item.labelKey)}</span>
                      )}
                    </Button>
                    {!collapsed && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); togglePin(path); }}
                        className="absolute right-1 top-1/2 -translate-y-1/2 p-1 rounded opacity-0 group-hover:opacity-100 focus-visible:opacity-100 hover:bg-muted text-muted-foreground hover:text-foreground transition-opacity focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        aria-label={locale === "es" ? "Desanclar" : "Unpin"}
                      >
                        <PinOff className="w-3 h-3" aria-hidden="true" />
                      </button>
                    )}
                  </div>
                );
              })}
              {!collapsed && <div className="my-2 mx-3 border-t border-border" />}
            </div>
          )}

          {navGroups.map((group) => {
            const GroupIcon = group.icon;
            const isOpen = openGroups[group.key] ?? false;
            const groupActive = isGroupActive(group);

            // Hide entire group if its feature flag is not available
            if (group.feature && !hasFeature(group.feature) && !user?.is_super_admin) return null;

            return (
              <div key={group.key}>
                {/* Group Header */}
                <button
                  onClick={() => !collapsed && toggleGroup(group.key)}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold uppercase tracking-wider transition-colors ${
                    groupActive ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
                  }`}
                  data-testid={`nav-group-${group.key}`}
                >
                  <GroupIcon className="w-4 h-4 flex-shrink-0" />
                  {!collapsed && (
                    <>
                      <span className="flex-1 text-left">{t(group.labelKey)}</span>
                      <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? '' : '-rotate-90'}`} />
                    </>
                  )}
                </button>

                {/* Group Items */}
                <AnimatePresence initial={false}>
                  {(isOpen || collapsed) && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.15 }}
                      className="overflow-hidden"
                    >
                      {group.items.map((item) => {
                        // D-S17-017: hide items with roleRequired when the user's role
                        // is not in the allowed list. Super-admins always pass.
                        if (item.roleRequired && !user?.is_super_admin &&
                            !item.roleRequired.includes(user?.role)) {
                          return null;
                        }
                        const Icon = item.icon;
                        const active = isActive(item.path);
                        const isLocked = item.feature && !hasFeature(item.feature);
                        const itemPinned = isPinned(item.path);
                        return (
                          <div key={item.path} className="group/item relative">
                            <Button
                              variant="ghost"
                              size="sm"
                              className={`w-full justify-start gap-2 h-8 ${collapsed ? 'px-3' : 'px-4 ml-3 pr-8'} ${
                                active ? 'bg-primary/10 text-primary font-medium' : 'text-muted-foreground hover:text-foreground'
                              } ${isLocked ? 'opacity-60' : ''}`}
                              onClick={() => { navigate(item.path); setMobileOpen(false); }}
                              data-testid={`nav-${item.labelKey.split('.').pop().replace(/_/g, '-')}`}
                            >
                              <Icon className={`w-4 h-4 flex-shrink-0 ${active ? 'text-primary' : ''}`} aria-hidden="true" />
                              {!collapsed && (
                                <>
                                  <span className="truncate text-xs flex-1 text-left">{t(item.labelKey)}</span>
                                  {isLocked && (
                                    <Badge
                                      onClick={(e) => { e.stopPropagation(); navigate("/billing"); setMobileOpen(false); }}
                                      title={locale === "es" ? "Disponible en Plan Professional — Ver planes" : "Available in Professional plan — View plans"}
                                      className="flex-shrink-0 px-1.5 py-0 h-4 text-[9px] font-bold bg-primary/15 text-primary border border-primary/40 hover:bg-primary/25 cursor-pointer"
                                      aria-label={locale === "es" ? "Disponible en Plan Professional, ver planes" : "Available in Professional plan, view plans"}
                                    >
                                      PRO
                                    </Badge>
                                  )}
                                </>
                              )}
                            </Button>
                            {!collapsed && !isLocked && (
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); togglePin(item.path); }}
                                className={`absolute right-1 top-1/2 -translate-y-1/2 p-1 rounded transition-opacity hover:bg-muted text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                                  itemPinned ? 'opacity-100 text-primary' : 'opacity-0 group-hover/item:opacity-100 focus-visible:opacity-100'
                                }`}
                                aria-label={itemPinned
                                  ? (locale === "es" ? "Desanclar" : "Unpin")
                                  : (locale === "es" ? "Anclar" : "Pin")}
                                aria-pressed={itemPinned}
                              >
                                <Pin className={`w-3 h-3 ${itemPinned ? 'fill-current' : ''}`} aria-hidden="true" />
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </nav>
      </ScrollArea>

      {/* User Section */}
      <div className="p-3 border-t border-border">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className={`w-full justify-start gap-3 ${collapsed ? 'px-2' : ''}`} data-testid="user-menu-trigger">
              <Avatar className="w-7 h-7">
                <AvatarImage src={user?.picture} />
                <AvatarFallback className="bg-primary/10 text-primary text-xs">{user?.name?.charAt(0) || 'U'}</AvatarFallback>
              </Avatar>
              {!collapsed && (
                <div className="flex-1 text-left overflow-hidden">
                  <p className="text-xs font-medium truncate">{user?.name || user?.email}</p>
                  <p className="text-[10px] text-muted-foreground truncate">{user?.role}</p>
                </div>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="top" align="start" className="w-56">
            <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout} className="text-destructive">
              <LogOut className="w-4 h-4 mr-2" /> {t("auth.logout")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </>
  );

  return (
    <div className="flex h-screen bg-background" data-testid="app-layout">
      {/* Desktop Sidebar */}
      <aside
        className={`hidden md:flex flex-col border-r border-border bg-card transition-all duration-base ${collapsed ? 'w-16' : 'w-60'}`}
        aria-label={locale === "es" ? "Navegación principal" : "Main navigation"}
      >
        <SidebarContent />
        <button
          onClick={() => setCollapsed(!collapsed)}
          aria-label={collapsed
            ? (locale === "es" ? "Expandir barra lateral" : "Expand sidebar")
            : (locale === "es" ? "Colapsar barra lateral" : "Collapse sidebar")}
          className="hidden md:flex items-center justify-center h-8 border-t border-border text-muted-foreground hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
        >
          <ChevronLeft className={`w-4 h-4 transition-transform ${collapsed ? 'rotate-180' : ''}`} aria-hidden="true" />
        </button>
      </aside>

      {/* Mobile Overlay */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 0.5 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black z-40 md:hidden" onClick={() => setMobileOpen(false)} />
            <motion.aside initial={{ x: -280 }} animate={{ x: 0 }} exit={{ x: -280 }}
              className="fixed inset-y-0 left-0 w-64 z-50 flex flex-col bg-card border-r border-border md:hidden">
              <SidebarContent />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Skip-to-content link (keyboard a11y) */}
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:px-3 focus:py-2 focus:rounded focus:bg-primary focus:text-primary-foreground focus:shadow"
        >
          {locale === "es" ? "Saltar al contenido" : "Skip to content"}
        </a>

        {/* Top Bar */}
        <header className="h-14 border-b border-border bg-card flex items-center justify-between px-4 flex-shrink-0">
          <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn"
            aria-label={locale === "es" ? "Abrir menú" : "Open menu"}>
            <Menu className="w-5 h-5" />
          </Button>

          {/* Command Palette trigger (search) */}
          <button
            type="button"
            onClick={() => setCmdOpen(true)}
            className="hidden md:flex items-center gap-2 h-9 w-72 px-3 rounded-md border border-border bg-background hover:bg-muted transition-colors text-sm text-muted-foreground"
            aria-label={locale === "es" ? "Buscar (Ctrl+K)" : "Search (Ctrl+K)"}
            data-testid="cmdk-trigger"
          >
            <Search className="w-4 h-4" />
            <span className="flex-1 text-left">
              {locale === "es" ? "Buscar..." : "Search..."}
            </span>
            <kbd className="pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium opacity-100">
              {isMac ? "⌘K" : "Ctrl+K"}
            </kbd>
          </button>

          {/* Mobile search icon */}
          <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setCmdOpen(true)}
            aria-label={locale === "es" ? "Buscar" : "Search"}>
            <Search className="w-5 h-5" />
          </Button>

          <div className="flex-1" />
          <div className="flex items-center gap-1">
            {/* Language Switcher */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="lang-switcher"
                  title={locale === "es" ? "Cambiar idioma" : "Change language"}
                  aria-label={locale === "es" ? "Cambiar idioma" : "Change language"}>
                  <Globe className="w-5 h-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>{t("lang.title")}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setLocale("en")} className={locale === "en" ? "font-bold" : ""}>
                  {locale === "en" && <Check className="w-3 h-3 mr-2" />} {t("lang.en")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setLocale("es")} className={locale === "es" ? "font-bold" : ""}>
                  {locale === "es" && <Check className="w-3 h-3 mr-2" />} {t("lang.es")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Notifications */}
            <DropdownMenu open={showNotifs} onOpenChange={(open) => { setShowNotifs(open); if (open) fetchNotifications(); }}>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="relative" data-testid="notifications-bell"
                  title={locale === "es" ? "Notificaciones" : "Notifications"}
                  aria-label={locale === "es"
                    ? `Notificaciones${unreadCount > 0 ? `, ${unreadCount} sin leer` : ""}`
                    : `Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ""}`}>
                  <Bell className="w-5 h-5" />
                  {unreadCount > 0 && (
                    <span
                      className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center font-medium"
                      aria-hidden="true"
                    >
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80 max-h-96 overflow-y-auto">
                <div className="flex items-center justify-between px-2 py-1.5">
                  <DropdownMenuLabel className="p-0">{t("notif.title")}</DropdownMenuLabel>
                  {unreadCount > 0 && (
                    <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={markAllRead}>
                      <Check className="w-3 h-3 mr-1" />{t("notif.mark_all")}
                    </Button>
                  )}
                </div>
                <DropdownMenuSeparator />
                {notifications.length === 0 ? (
                  <div className="py-6 text-center text-sm text-muted-foreground">{t("notif.no_notifications")}</div>
                ) : (
                  notifications.map(n => (
                    <DropdownMenuItem key={n.notification_id} className={`flex flex-col items-start gap-1 py-2 ${!n.read ? 'bg-primary/5' : ''}`}>
                      <span className={`text-sm ${!n.read ? 'font-medium' : ''}`}>{n.title}</span>
                      <span className="text-xs text-muted-foreground line-clamp-2">{n.message}</span>
                    </DropdownMenuItem>
                  ))
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Theme Toggle */}
            <Button variant="ghost" size="icon" onClick={toggleTheme}
              title={locale === "es" ? "Cambiar tema" : "Switch theme"}
              aria-label={theme === "dark"
                ? (locale === "es" ? "Cambiar a tema claro" : "Switch to light theme")
                : (locale === "es" ? "Cambiar a tema oscuro" : "Switch to dark theme")}>
              {theme === "dark" ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
          </div>
        </header>

        {/* S14b: 2FA opt-in banner — solo si totp_enabled=false; dismissable por sesión */}
        <TwoFactorBanner />

        {/* Content */}
        <main id="main-content" className="flex-1 overflow-auto" tabIndex={-1}>
          {children}
        </main>
      </div>

      {/* Global command palette (⌘K) */}
      <CommandPalette open={cmdOpen} onOpenChange={setCmdOpen} />

      {/* AI Copilot floating panel */}
      <AICopilot />
    </div>
  );
};

export default SidebarLayout;
